import type { ComponentProps } from '../blocks';
import { CHART_COLORS, CHART_FONT, resolveCssColor, type ChartColors } from './colors';
import { xmlEscape } from './escape';
import { splitColor, splitPieces } from './split';

/**
 * Layout constants for the rasterized pie/donut chart. The chart occupies a
 * square region on the left; the legend runs down the right. `W` matches the
 * other chart rasterizers (720) so a pie sits the same width as an area/split.
 */
export const PIE_DIMS = {
  W: 720,
  /** Square chart region on the left (also the pie's bounding box). */
  chartSize: 220,
  /** Outer radius of the pie. */
  r: 100,
  /** Inner radius when `donut` is set (0 for a solid pie). */
  innerR: 58,
  /** Left column x of the legend, right of the chart region. */
  legendX: 260,
  legendLineH: 24,
} as const;

/** One computed slice: the SVG wedge path plus its angles, color index, and share. */
export interface PieSlice {
  label: string;
  value: number;
  color?: string;
  /** Share of the total in [0, 1]. */
  fraction: number;
  /** Percentage rounded to one decimal. */
  pct: number;
  /** SVG path for the wedge (solid pie) or annular segment (donut). */
  path: string;
  /** Cumulative start/end angle in degrees, clockwise from 12 o'clock (-90°). */
  startAngle: number;
  endAngle: number;
  /** Palette index for `splitColor` (mirrors the segment order). */
  colorIndex: number;
}

/** Pure coordinate/path math for a pie/donut — no React, no DOM, no color. */
export interface PieGeometry {
  slices: PieSlice[];
  cx: number;
  cy: number;
  r: number;
  innerR: number;
  total: number;
}

/** Point on a circle of radius `rad` at `angleDeg` (clockwise from 12 o'clock). */
function polar(cx: number, cy: number, rad: number, angleDeg: number): { x: number; y: number } {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + rad * Math.cos(a), y: cy + rad * Math.sin(a) };
}

/**
 * Compute pie/donut geometry: one wedge (or annular segment) path per slice.
 * Angles start at 12 o'clock and sweep clockwise. A single full-circle slice is
 * drawn as two half-arcs because SVG's `A` command can't express a 360° arc in
 * one segment (start == end would collapse to nothing). Empty/all-zero segments
 * yield no slices — the caller renders "No data".
 */
export function pieGeometry(
  segments: ComponentProps['Pie']['segments'],
  donut = false,
  dims: typeof PIE_DIMS = PIE_DIMS,
): PieGeometry {
  const { r } = dims;
  const cx = dims.chartSize / 2;
  const cy = dims.chartSize / 2;
  const innerR = donut ? dims.innerR : 0;
  const { pieces, total } = splitPieces(segments);

  const slices: PieSlice[] = [];
  let cursor = 0;
  pieces.forEach((p, i) => {
    if (p.fraction <= 0) return;
    const startAngle = cursor * 360;
    cursor += p.fraction;
    const endAngle = cursor * 360;
    slices.push({
      label: p.label,
      value: p.value,
      color: p.color,
      fraction: p.fraction,
      pct: p.pct,
      startAngle,
      endAngle,
      colorIndex: i,
      path: wedgePath(cx, cy, r, innerR, startAngle, endAngle),
    });
  });

  return { slices, cx, cy, r, innerR, total };
}

/**
 * SVG path for one slice from `startAngle` to `endAngle` (degrees, clockwise).
 * A near-full sweep (>= 360°, i.e. a single 100% slice) is drawn as two arcs so
 * the `A` command never has coincident endpoints. `innerR > 0` yields an annular
 * (donut) segment; `innerR === 0` yields a solid wedge to the center.
 */
function wedgePath(cx: number, cy: number, r: number, innerR: number, startAngle: number, endAngle: number): string {
  const sweep = endAngle - startAngle;
  // Full circle: split into two 180° arcs (a single 360° A-arc is degenerate).
  if (sweep >= 360) {
    const mid = startAngle + 180;
    const a = polar(cx, cy, r, startAngle);
    const b = polar(cx, cy, r, mid);
    const outer =
      `M${a.x.toFixed(2)},${a.y.toFixed(2)}` +
      `A${r},${r} 0 1 1 ${b.x.toFixed(2)},${b.y.toFixed(2)}` +
      `A${r},${r} 0 1 1 ${a.x.toFixed(2)},${a.y.toFixed(2)}Z`;
    if (innerR <= 0) return outer;
    const ia = polar(cx, cy, innerR, startAngle);
    const ib = polar(cx, cy, innerR, mid);
    // Inner ring wound the opposite direction (sweep flag 0) to punch the hole.
    const inner =
      `M${ia.x.toFixed(2)},${ia.y.toFixed(2)}` +
      `A${innerR},${innerR} 0 1 0 ${ib.x.toFixed(2)},${ib.y.toFixed(2)}` +
      `A${innerR},${innerR} 0 1 0 ${ia.x.toFixed(2)},${ia.y.toFixed(2)}Z`;
    return `${outer} ${inner}`;
  }

  const largeArc = sweep > 180 ? 1 : 0;
  const o1 = polar(cx, cy, r, startAngle);
  const o2 = polar(cx, cy, r, endAngle);
  if (innerR <= 0) {
    return (
      `M${cx.toFixed(2)},${cy.toFixed(2)}` +
      `L${o1.x.toFixed(2)},${o1.y.toFixed(2)}` +
      `A${r},${r} 0 ${largeArc} 1 ${o2.x.toFixed(2)},${o2.y.toFixed(2)}Z`
    );
  }
  const i1 = polar(cx, cy, innerR, endAngle);
  const i2 = polar(cx, cy, innerR, startAngle);
  return (
    `M${o1.x.toFixed(2)},${o1.y.toFixed(2)}` +
    `A${r},${r} 0 ${largeArc} 1 ${o2.x.toFixed(2)},${o2.y.toFixed(2)}` +
    `L${i1.x.toFixed(2)},${i1.y.toFixed(2)}` +
    `A${innerR},${innerR} 0 ${largeArc} 0 ${i2.x.toFixed(2)},${i2.y.toFixed(2)}Z`
  );
}

/**
 * Standalone pie/donut SVG for rasterization (Slack PNGs). Mirrors the React
 * `PieChart` primitive: circular slices on the left + a percentage legend on the
 * right. An empty or all-zero total renders a "No data to chart." message like
 * the split/area rasterizers.
 */
export function pieSvg(props: ComponentProps['Pie'], colors: ChartColors = CHART_COLORS): string {
  const { title, segments, unit = '', donut = false } = props;
  const { W, chartSize, legendX, legendLineH } = PIE_DIMS;
  const titleH = title ? 34 : 8;
  const unitSuffix = unit ? ` ${unit}` : '';
  const geo = pieGeometry(segments, donut);

  // Height is the taller of the chart region and the legend rows.
  const legendH = segments.length * legendLineH;
  const bodyH = Math.max(chartSize, legendH + 8);
  const totalH = titleH + bodyH + 8;

  const header = (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '');

  if (geo.total <= 0) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
      `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
      header +
      `<text x="${W / 2}" y="${totalH / 2}" text-anchor="middle" font-size="13" fill="${colors.subtext}">No data to chart.</text></svg>`
    );
  }

  const slices = geo.slices
    .map(
      (s) =>
        `<path d="${s.path}" fill="${xmlEscape(resolveCssColor(splitColor(s.color, s.colorIndex), colors))}"/>`,
    )
    .join('');

  const legend = geo.slices
    .map((s, row) => {
      const y = row * legendLineH + 16;
      const swatch = `<rect x="${legendX}" y="${y - 9}" width="11" height="11" rx="3" fill="${xmlEscape(resolveCssColor(splitColor(s.color, s.colorIndex), colors))}"/>`;
      const text = `${s.label}  ${s.pct}%  ${s.value.toLocaleString()}${unitSuffix}`;
      return swatch + `<text x="${legendX + 18}" y="${y}" font-size="13" fill="${colors.text}">${xmlEscape(text)}</text>`;
    })
    .join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
    `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
    header +
    `<g transform="translate(0 ${titleH})">` +
    slices +
    legend +
    `</g></svg>`
  );
}
